const STABLE_ID_STORAGE_KEY = "statsig_stable_id";
const NEW_CTA_COPY_EXPERIMENT_NAME = "new_cta_copy";
const CTA_CONTROL_COPY = "Get Started";
const CTA_VARIANT_COPY = "Start Free 7-Day Trial";
const STATSIG_ENV_TIERS = {
  PRODUCTION: "production",
  STAGING: "staging",
  DEVELOPMENT: "development"
};

const normalize = value =>
  `${value || ""}`
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const normalizeTier = value => {
  const normalized = normalize(value);

  if (normalized === "prod" || normalized === STATSIG_ENV_TIERS.PRODUCTION) {
    return STATSIG_ENV_TIERS.PRODUCTION;
  }

  if (normalized === "stage" || normalized === STATSIG_ENV_TIERS.STAGING) {
    return STATSIG_ENV_TIERS.STAGING;
  }

  if (normalized === "dev" || normalized === STATSIG_ENV_TIERS.DEVELOPMENT) {
    return STATSIG_ENV_TIERS.DEVELOPMENT;
  }

  return null;
};

export const getStatsigEnvironmentTier = hostname => {
  const normalizedHost = `${hostname || ""}`.trim().toLowerCase();

  if (normalizedHost === "localhost" || normalizedHost === "127.0.0.1") {
    return STATSIG_ENV_TIERS.DEVELOPMENT;
  }

  if (
    normalizedHost.includes("staging-spocket.co") ||
    normalizedHost.includes("staging-api-heroku.spocket.co") ||
    normalizedHost.includes("api2.aws.staging-spocket.co")
  ) {
    return STATSIG_ENV_TIERS.STAGING;
  }

  if (
    normalizedHost === "app.spocket.co" ||
    normalizedHost === "spocket.co" ||
    normalizedHost === "www.spocket.co"
  ) {
    return STATSIG_ENV_TIERS.PRODUCTION;
  }

  return null;
};

const resolveStatsigEnvironmentTier = () => {
  if (typeof window !== "undefined" && window.location) {
    const hostTier = getStatsigEnvironmentTier(window.location.hostname);

    if (hostTier) {
      return hostTier;
    }
  }

  const appTier = normalizeTier(process.env.REACT_APP_ENV);

  if (appTier) {
    return appTier;
  }

  const nodeTier = normalizeTier(process.env.NODE_ENV);

  return nodeTier || STATSIG_ENV_TIERS.DEVELOPMENT;
};

const createStableId = () => {
  if (typeof window !== "undefined" && window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
};

export const getOrCreateStatsigStableId = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return "server";
  }

  try {
    const storedStableId = window.localStorage.getItem(STABLE_ID_STORAGE_KEY);

    if (storedStableId) {
      return storedStableId;
    }

    const newStableId = createStableId();
    window.localStorage.setItem(STABLE_ID_STORAGE_KEY, newStableId);

    return newStableId;
  } catch {
    return "fallback";
  }
};

const getLoggedInUser = () => {
  if (typeof window === "undefined" || !window.localStorage) return null;

  try {
    const userId = localStorage.getItem("shop_id");
    if (!userId) return null;

    const email = localStorage.getItem("user_email") || undefined;
    return { userId, email };
  } catch {
    return null;
  }
};

export const getStatsigUser = () => {
  const loggedIn = getLoggedInUser();
  const stableID = getOrCreateStatsigStableId();

  return {
    ...(loggedIn ? { userID: loggedIn.userId, ...(loggedIn.email ? { email: loggedIn.email } : {}) } : {}),
    customIDs: { stableID }
  };
};

const getUrlCtaOverride = () => {
  if (typeof window === "undefined") return null;

  const param = normalize(new URLSearchParams(window.location.search).get("statsig_cta"));

  if (param === "variant") return CTA_VARIANT_COPY;
  if (param === "control") return CTA_CONTROL_COPY;

  return null;
};

const getForcedCtaCopy = () => {
  const urlOverride = getUrlCtaOverride();
  if (urlOverride) return urlOverride;

  const forcedValue = normalize(process.env.REACT_APP_STATSIG_FORCE_NEW_CTA_COPY);

  if (forcedValue === "variant") return CTA_VARIANT_COPY;
  if (forcedValue === "control") return CTA_CONTROL_COPY;

  return null;
};

export const getStatsigOptions = () => {
  const options = {
    environment: {
      tier: resolveStatsigEnvironmentTier()
    }
  };

  const forcedCtaCopy = getForcedCtaCopy();

  if (forcedCtaCopy) {
    options.overrideAdapter = {
      getExperimentOverride(currentExperiment) {
        if (currentExperiment.name !== NEW_CTA_COPY_EXPERIMENT_NAME) {
          return null;
        }

        return {
          ...currentExperiment,
          value: {
            ...(currentExperiment.value || {}),
            buttonText: forcedCtaCopy
          }
        };
      }
    };
  }

  return options;
};

export const STATSIG_KEYS = {
  STABLE_ID_STORAGE_KEY,
  NEW_CTA_COPY_EXPERIMENT_NAME,
  CTA_CONTROL_COPY,
  CTA_VARIANT_COPY,
  STATSIG_ENV_TIERS
};
